import type { ReactNode } from 'react';
import {
  Banner,
  Box,
  Button,
  Link,
  List,
  ListItem,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderIconTile,
  PageHeaderTabs,
  PageHeaderTitle,
  Spinner,
  TabNav,
  TabNavList,
  TabNavTab,
  Tooltip,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAuth } from '@/services/auth';
import { useSqlImportsByDatabase } from '@/services/db/sql-import/queries';
import { SqlImportDatabase } from '@/services/db/sql-import/entity';
import { snowflakeConfigActionHref, snowflakePasswordConfigs, type SnowflakePasswordConfig } from './snowflake-configs';

const FEEDBACK_URL = 'https://custio.notion.site/1a14302f4c2b80a18b10e8b6ec44ba67?pvs=105';
const UI_UPDATE_DOCS_URL = 'https://docs.customer.io/accounts-and-workspaces/2025-ui-update/';
const SNOWFLAKE_SYNC_DOCS_URL = 'https://docs.customer.io/snowflake-sync/';

/** `none` = a URL-only sub-tab (developers / webhooks): the shell renders with no tab active. */
export type IntegrationsTab = 'connections' | 'directory' | 'none';

type InviteTeamMemberButtonProps = {
  canEditTeamMembers: boolean;
  isAccountConfirmed: boolean;
};

// Ember marks this link `@isExternal={{true}}` (`integrations/all/template.gts:39`) to escape its own
// router for a URL it doesn't serve. Hydra owns `/settings/team`, so the link stays internal here and
// navigates in place through the Pluma link adapter, like the sibling links in this header.
function InviteTeamMemberButton({ canEditTeamMembers, isAccountConfirmed }: InviteTeamMemberButtonProps) {
  const isPendingConfirmation = !isAccountConfirmed;
  const isDisabled = !canEditTeamMembers || isPendingConfirmation;
  const disabledReason = isPendingConfirmation
    ? 'Please confirm your email address before inviting team members.'
    : 'Contact your admin to access this feature.';

  return (
    <Tooltip content={disabledReason} isDisabled={!isDisabled} isCustom={true}>
      {(context) => (
        <Button
          ref={context.reference.setReference}
          {...context.reference.getReferenceProps()}
          data-test-integrations-header-button="manage-team-member"
          variant="secondary"
          href="/settings/team"
          isDisabled={isDisabled}
        >
          Invite team member
        </Button>
      )}
    </Tooltip>
  );
}

type SnowflakeWarningBannerProps = {
  configs: SnowflakePasswordConfig[];
  actionHref: string;
};

function SnowflakeWarningBanner({ configs, actionHref }: SnowflakeWarningBannerProps) {
  const intro =
    configs.length > 1
      ? 'The following Snowflake configurations use password authentication and need to be updated:'
      : 'The following Snowflake configuration uses password authentication and needs to be updated:';

  return (
    <Box px="300">
      <Banner
        variant="caution"
        icon="caution"
        mb="250"
        title="Action required: Your Snowflake connection still uses password authentication, which will be retired soon."
        action={{ label: 'Update database configuration', href: actionHref }}
        data-test-snowflake-password-warning
        description={
          <>
            {intro}
            <List type="unordered" mt="200" mb="200">
              {configs.map((config) => (
                <ListItem key={config.href}>
                  <Link href={config.href}>{config.name}</Link>
                </ListItem>
              ))}
            </List>
            To keep your data flowing, switch to key-pair auth.{' '}
            <Link href={SNOWFLAKE_SYNC_DOCS_URL} variant="secondary" isExternal>
              Learn more here
            </Link>
            .
          </>
        }
      />
    </Box>
  );
}

function FeedbackBanner() {
  return (
    <Box px="300">
      <Banner
        mb="250"
        title="Tell us what you think!"
        variant="feature"
        action={{ label: 'Share feedback', href: FEEDBACK_URL, isExternal: true }}
        description={
          <>
            We're working on our integration pages and need your feedback to help us build the best experience for you.{' '}
            <Link variant="primary" href={UI_UPDATE_DOCS_URL} isUnderlined isExternal>
              Learn More
            </Link>
          </>
        }
      />
    </Box>
  );
}

type IntegrationsBannerProps = {
  workspaceId: string;
};

/**
 * The single banner slot above the tabs, in strict order: a spinner while the SQL-import
 * reads are in flight, then the Snowflake password-auth warning when any active Snowflake
 * import still uses password auth, otherwise the feedback banner once the reads have settled.
 *
 * The reads use the id the route view already resolved from the router location (never a fresh
 * `window.location` read) — that URL lags the router location during the `/workspaces/last`
 * redirect, so re-reading it here would race the redirect.
 */
function IntegrationsBanner({ workspaceId }: IntegrationsBannerProps) {
  const { byDatabase, isPending } = useSqlImportsByDatabase(Number(workspaceId));

  if (isPending) {
    return (
      <Box px="300">
        <Spinner mb="250" />
      </Box>
    );
  }

  const configs = snowflakePasswordConfigs(byDatabase[SqlImportDatabase.snowflake], workspaceId);
  if (configs.length > 0) {
    return <SnowflakeWarningBanner configs={configs} actionHref={snowflakeConfigActionHref(workspaceId)} />;
  }

  return <FeedbackBanner />;
}

export type IntegrationsShellProps = {
  workspaceId: string;
  activeTab: IntegrationsTab;
  overviewHref: string;
  directoryHref: string;
  children: ReactNode;
};

export function IntegrationsShell({
  workspaceId,
  activeTab,
  overviewHref,
  directoryHref,
  children,
}: IntegrationsShellProps) {
  const { hasPermission } = usePermissions();
  const { account } = useAuth();
  const showAddIntegration = activeTab !== 'directory';

  return (
    <Page flexGrow="1">
      <PageHeader>
        <PageHeaderIconTile name="integrations" />
        <PageHeaderTitle data-test-enabled-integrations-title>Integrations</PageHeaderTitle>
        <PageHeaderActions>
          <InviteTeamMemberButton
            canEditTeamMembers={hasPermission('account/team_members/edit')}
            isAccountConfirmed={account.confirmed === true}
          />
          {showAddIntegration ? (
            <Button
              data-test-integrations-header-button="add-integration"
              variant="primary"
              icon="add"
              href={directoryHref}
            >
              Add Integration
            </Button>
          ) : null}
        </PageHeaderActions>
        <PageHeaderTabs>
          <IntegrationsBanner workspaceId={workspaceId} />
          <TabNav aria-label="Integrations" data-test-integrations-header-tabs>
            <TabNavList>
              <TabNavTab href={overviewHref} isActive={activeTab === 'connections'} icon="connections">
                Connections
              </TabNavTab>
              <TabNavTab href={directoryHref} isActive={activeTab === 'directory'} icon="blocks">
                Directory
              </TabNavTab>
            </TabNavList>
          </TabNav>
        </PageHeaderTabs>
      </PageHeader>
      <PageBody>{children}</PageBody>
    </Page>
  );
}
